import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { formatPoints } from "@/lib/format";

export default function Profile() {
    const { username } = useParams();
    const [p, setP] = useState(null);

    useEffect(() => {
        api.get(`/users/${username}`).then((r) => setP(r.data)).catch(() => setP(false));
    }, [username]);

    if (p === false) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-p2w-gray">Usuario no encontrado</div>;
    if (!p) return <div className="max-w-3xl mx-auto px-6 py-20"><div className="h-64 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" /></div>;

    const xpToNext = (p.level + 1) * 200;
    const xpProgress = Math.min(100, (p.xp / xpToNext) * 100);

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="profile-page">
            <div className="relative overflow-hidden rounded-2xl border border-p2w-line bg-gradient-to-br from-p2w-surface to-p2w-elevated p-8">
                <div className="glow-orb bg-p2w-purple w-[300px] h-[300px] -top-20 -right-20 opacity-30" />
                <div className="relative flex flex-col sm:flex-row items-start gap-6">
                    <Avatar className="w-24 h-24 ring-2 ring-p2w-green/30 shadow-glow-green">
                        <AvatarImage src={p.avatar_url} alt={p.username} />
                        <AvatarFallback className="bg-p2w-purple text-2xl text-white">
                            {(p.username || "?").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-sora font-extrabold text-3xl">@{p.username}</h1>
                            {p.is_creator && (
                                <span className="px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider bg-p2w-purple/20 text-p2w-purple border border-p2w-purple/40">
                                    Creador
                                </span>
                            )}
                        </div>
                        <div className="mt-2 text-p2w-gray text-sm">
                            {p.followers} seguidores · {p.following} siguiendo · {p.predictions} predicciones
                        </div>
                        <div className="mt-5">
                            <div className="flex items-center justify-between text-xs text-p2w-gray mb-1.5">
                                <span>Nivel {p.level}</span>
                                <span className="number-tabular">
                                    {p.xp} / {xpToNext} XP
                                </span>
                            </div>
                            <Progress value={xpProgress} className="h-2 bg-p2w-elevated [&>div]:bg-p2w-green" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <Stat label="Aciertos" value={`${p.win_rate}%`} accent />
                <Stat label="Ganadas" value={p.wins} />
                <Stat label="Predicciones" value={p.predictions} />
            </div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5">
            <div className="text-xs uppercase tracking-widest text-p2w-gray">{label}</div>
            <div className={`mt-2 font-sora font-extrabold text-3xl number-tabular ${accent ? "text-p2w-green" : "text-white"}`}>
                {value}
            </div>
        </div>
    );
}
